import { useKnockClient } from "@knocklabs/react";
import { requestInAppReview as requestInAppReviewAdapter } from "@src/appV2/NativeAdapters/inAppReview.adapter";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";
import { useCallback } from "react";

import {
  KNOCK_LAST_REVIEW_PROMPT_SHOWN_AT_ATTRIBUTE,
  KNOCK_REVIEW_PROMPT_ACTION_TYPE_ATTRIBUTE,
  REVIEW_PROMPT_ACTION_TYPES,
} from "./constants";

export interface RequestInAppReviewOptions {
  /**
   * Advance the cooldown pointer even when the native call throws (default:
   * only on success). Set on the willing-rater direct-fire path, where there is
   * no user gesture and the surface re-mounts every eligible session — a native
   * failure that left the pointer untouched would re-fire on every mount. The
   * modal Yes path keeps success-only semantics: the user explicitly tapped,
   * and a failure there is recoverable next time.
   */
  advanceCooldownOnAttempt?: boolean;
}

export interface UseRequestInAppReviewResult {
  /** Requests the native in-app review sheet. No-op on web / flag off / import failure. */
  requestInAppReview: (options?: RequestInAppReviewOptions) => Promise<void>;
}

export function useRequestInAppReview(): UseRequestInAppReviewResult {
  const isEnabled = useCbhFlag(CbhFeatureFlag.WORKPLACE_RATING_PROMPT, { defaultValue: false });
  const knockClient = useKnockClient();

  const requestInAppReview = useCallback(
    async (options?: RequestInAppReviewOptions) => {
      if (!isEnabled || !Platform.isMobile) {
        return;
      }

      const recordPromptedAt = async () => {
        try {
          await knockClient.user.identify({
            [KNOCK_LAST_REVIEW_PROMPT_SHOWN_AT_ATTRIBUTE]: new Date().toISOString(),
            [KNOCK_REVIEW_PROMPT_ACTION_TYPE_ATTRIBUTE]: REVIEW_PROMPT_ACTION_TYPES.rate,
          });
        } catch (error) {
          logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
        }
      };

      try {
        await requestInAppReviewAdapter();
      } catch (error) {
        logError(APP_V2_APP_EVENTS.RATING_PROMPT_NATIVE_REQUEST_FAILED, { error });
        if (options?.advanceCooldownOnAttempt) {
          await recordPromptedAt();
        }

        return;
      }

      await recordPromptedAt();
    },
    [isEnabled, knockClient]
  );

  return { requestInAppReview };
}
